/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   4/25/22
 */

import React from "react";
import { Button, FormControl, IconButton, MenuItem, Select, Stack, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
	EmployeesFilter,
	EmployeesFilterAttribute,
	EmployeesFilterItem,
	EmployeesFilterOperation
} from "../interfaces/Global";
import CloseIcon from "@mui/icons-material/Close";
import EmployeesControl from "../controls/EmployeesControl";
import TypeaheadControl from "../controls/TypeaheadControl";
import {
	EmployeeDepartmentsQuery,
	EmployeeDivisionsQuery,
	EmployeeJobTitlesQuery,
	EmployeeLocationsQuery,
	EmployeeProductsQuery,
	EmployeeTagsQuery
} from "../graphql/queries/employees";
import CountriesControl from "../controls/CountriesControl";

export interface EmployeesFilterFormProps {
	filter: EmployeesFilter
	setFilter: (filter: EmployeesFilter) => void
	allowZeroConditions?: boolean
	disabled?: boolean
}

export default function EmployeesFilterForm(props: EmployeesFilterFormProps) {

	return (
		<Stack spacing={2}>
			{props.filter.length === 0 && <Stack />}
			{props.filter.map((item, index) =>
				<FilterItem
					key={index + item[0] + item[1]}
					index={index}
					item={item}
					set={(item) => {
						const newFilter = [ ...props.filter ];
						newFilter[index] = item;
						props.setFilter(newFilter);
					}}
					allowRemove={props.allowZeroConditions || index !== 0}
					disabled={props.disabled}
					remove={() => {
						props.setFilter(props.filter.filter((item, i) => i !== index));
					}}
				/>)
			}
			<Stack direction={"row"}>
				<Button
					variant="outlined"
					size={"small"}
					startIcon={<AddIcon />}
					disabled={props.disabled}
					onClick={() => {
						props.setFilter([
							...props.filter,
							["and" as EmployeesFilterOperation, "managerId" as EmployeesFilterAttribute, []]
						]);
					}}
				>
					Add condition
				</Button>
			</Stack>
		</Stack>
	);

}

export interface FilterItemProps {
	item: EmployeesFilterItem,
	index: number,
	set: (item: EmployeesFilterItem) => void,
	remove: () => void
	allowRemove: boolean
	disabled?: boolean
}

function FilterItem({ item, index, set, remove, allowRemove, disabled }: FilterItemProps) {

	const [op, attribute, values] = item;

	const submit = (o: EmployeesFilterOperation, a: EmployeesFilterAttribute, v: string[]) => { set([o, a, v]) };

	return (
		<Stack direction={"row"} spacing={3}>
			{index !== 0 &&
			<Stack>
				<FilterItemOperationSelector
					disabled={disabled}
					op={op}
					set={(op) => submit(op, attribute, values)}
				/>
			</Stack>}
			<Stack>
				<FilterItemAttributeSelector
					disabled={disabled}
					attribute={attribute}
					set={(attribute) => submit(op, attribute, [])}
				/>
			</Stack>
			<Stack flex={1}>
				<FilterItemValuesSelector
					disabled={disabled}
					attribute={attribute}
					values={values}
					set={(values) => submit(op, attribute, values)}
				/>
			</Stack>
			{allowRemove &&
			<Stack>
				<IconButton disabled={disabled} onClick={remove} size={"small"}>
					<Tooltip title={"Remove Condition"}>
						<CloseIcon />
					</Tooltip>
				</IconButton>
			</Stack>}
		</Stack>
	)
}

function FilterItemOperationSelector({ op, set, disabled }: { op: EmployeesFilterOperation, set: (op: EmployeesFilterOperation) => void, disabled?: boolean }) {

	return (
		<FormControl variant={"standard"}>
			<Select
				value={op === "+" ? "or" : op === "-" ? "not" : op}
				disabled={disabled}
				onChange={event => {
					set(event.target.value as EmployeesFilterOperation);
				}}
				sx={{ width: "60px" }}
			>
				<MenuItem value={"and"}>AND</MenuItem>
				<MenuItem value={"or"}>OR</MenuItem>
				<MenuItem value={"and not"}>NOT</MenuItem>
			</Select>
		</FormControl>
	);
}

function FilterItemAttributeSelector({ attribute, set, disabled }: { attribute: EmployeesFilterAttribute, set: (attribute: EmployeesFilterAttribute) => void, disabled?: boolean }) {

	return (
		<FormControl variant={"standard"}>
			<Select
				value={attribute}
				disabled={disabled}
				onChange={event => {
					set(event.target.value as EmployeesFilterAttribute);
				}}
				sx={{ width: "200px" }}
			>
				<MenuItem value={"managerId"}>Direct Reports Of</MenuItem>
				<MenuItem value={"id"}>Individual Employees</MenuItem>
				<MenuItem value={"location"}>Locations</MenuItem>
				<MenuItem value={"company"}>Products</MenuItem>
				<MenuItem value={"jobTitle"}>JobTitles</MenuItem>
				<MenuItem value={"department"}>Departments</MenuItem>
				<MenuItem value={"division"}>Divisions</MenuItem>
				<MenuItem value={"countryCode"}>Countries</MenuItem>
				<MenuItem value={"tags"}>Tags</MenuItem>
			</Select>
		</FormControl>
	);
}

function FilterItemValuesSelector({ attribute, values, set, disabled }: { attribute: EmployeesFilterAttribute, values: string[], set: (values: string[]) => void, disabled?: boolean  }) {

	return (
		<FormControl variant={"standard"} sx={{ flexGrow: 2 }}>
			{(attribute === "managerId" || attribute === "id") &&
				<EmployeesControl
					values={values}
					onChange={(values) => set(values)}
					disabled={disabled}
				/>
			}
			{attribute === "location" &&
				<TypeaheadControl
					values={values}
					onChange={(values) => set(values)}
					query={EmployeeLocationsQuery}
					disabled={disabled}
				/>
			}
			{attribute === "company" &&
				<TypeaheadControl
					values={values}
					onChange={(values) => set(values)}
					query={EmployeeProductsQuery}
					disabled={disabled}
				/>
			}
			{attribute === "jobTitle" &&
				<TypeaheadControl
					values={values}
					onChange={(values) => set(values)}
					query={EmployeeJobTitlesQuery}
					disabled={disabled}
				/>
			}
			{attribute === "department" &&
				<TypeaheadControl
					values={values}
					onChange={(values) => set(values)}
					query={EmployeeDepartmentsQuery}
					disabled={disabled}
				/>
			}
			{attribute === "division" &&
				<TypeaheadControl
					values={values}
					onChange={(values) => set(values)}
					query={EmployeeDivisionsQuery}
					disabled={disabled}
				/>
			}
			{attribute === "countryCode" &&
				<CountriesControl
					values={values}
					onChange={(values) => set(values)}
					disabled={disabled}
				/>
			}
			{attribute === "tags" &&
				<TypeaheadControl
					free
					values={values}
					onChange={(values) => set(values)}
					requestPolicy={"cache-and-network"}
					query={EmployeeTagsQuery}
					disabled={disabled}
				/>}
		</FormControl>
	);
}